import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Shifts() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/shifts").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/users").then(({ data }) => setUsers(data)); }, []);
  const del = async (id) => { if (!window.confirm("Delete shift?")) return; await api.delete(`/shifts/${id}`); load(); };
  // Group by date
  const grouped = {};
  rows.forEach((s) => { const d = (s.start_at || "").slice(0, 10); (grouped[d] = grouped[d] || []).push(s); });
  const dates = Object.keys(grouped).sort();
  const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return iso; } };
  return (
    <div className="space-y-6" data-testid="shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Operations</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Shift Schedule</h1>
        </div>
        {canManage && <button onClick={() => setOpen(true)} data-testid="new-shift" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> New shift</button>}
      </div>
      <div className="space-y-3">
        {dates.map((d) => (
          <div key={d} className="card-surface p-4">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {grouped[d].map((s) => (
                <div key={s.id} data-testid={`sh-${s.id}`} className="bg-[#0b0d12] border border-[#2a3447] rounded-lg p-3 flex items-start gap-3 hover:border-[color:var(--accent)]/40">
                  <div className="w-2 h-14 rounded-full" style={{ background: s.color || "var(--accent)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.user_name}</div>
                    <div className="text-xs text-slate-400">{s.title}</div>
                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--accent)] mt-1">{fmt(s.start_at)} — {fmt(s.end_at)}</div>
                    {s.notes && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{s.notes}</div>}
                  </div>
                  {canManage && <button onClick={() => del(s.id)} data-testid={`sh-del-${s.id}`} className="text-slate-500 hover:text-[#ff3b5c]"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {dates.length === 0 && <div className="card-surface p-10 text-center text-slate-500"><Calendar size={24} className="inline mr-2" /> No shifts scheduled yet.</div>}
      </div>
      {open && <NewShift users={users} onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewShift({ users, onClose, onDone }) {
  const [f, setF] = useState({ user_id: users[0]?.id || "", title: "Shift", start_at: "", end_at: "", notes: "", color: "var(--accent)" });
  const submit = async (e) => { e.preventDefault(); await api.post("/shifts", f); onDone(); toast.success("Shift created"); };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="sh-modal">
        <h2 className="text-xl font-bold mb-4">New shift</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Assign to</div><select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="sh-user" required className={inp}>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Title</div><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="sh-title" className={inp} /></label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label><div className="text-xs uppercase text-slate-500 mb-1">Start</div><input type="datetime-local" required value={f.start_at} onChange={(e) => setF({ ...f, start_at: e.target.value })} data-testid="sh-start" className={inp} /></label>
          <label><div className="text-xs uppercase text-slate-500 mb-1">End</div><input type="datetime-local" required value={f.end_at} onChange={(e) => setF({ ...f, end_at: e.target.value })} data-testid="sh-end" className={inp} /></label>
        </div>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Notes</div><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="sh-notes" className={inp + " resize-none"} /></label>
        <div className="flex items-center gap-2 mb-3"><span className="text-xs text-slate-500">Color</span><input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="sh-color" className="w-10 h-8 bg-transparent border border-slate-700 rounded" /></div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button>
          <button type="submit" data-testid="sh-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Create</button>
        </div>
      </form>
    </div>
  );
}
